import React, { useEffect, useState } from "react";
import { supportedBrands } from "./customization/supported-brands";
import { styleObject } from "./customization/style-object";
import { ariaLabels } from "./customization/aria-labels";
import { AdyenIframeTemplate } from "./adyen-iframe-template";
import { AdyenInstance } from "./use-adyen-instance";
import { useEqualState } from "../../../../../hooks";
import {
  CbObjOnBinLookup,
  CbObjOnBinValue,
  CbObjOnBrand,
} from "@adyen/adyen-web/dist/types/components/internal/SecuredFields/lib/types";
import { BrandObject } from "@adyen/adyen-web/dist/types/components/Card/types";
import SecuredFieldsElement from "@adyen/adyen-web/dist/types/components/SecuredFields";
import { AdyenInstanceControls, CardDataBinInfo, CardDataValidCallbackData } from "./types";
import { createSecuredFields } from "./create-secured-fields";
import type { TranslatedValue } from "@swo/storefront";

export type AdyenEnhancedFormT = ReturnType<typeof useAdyenEnhancedForm>;

const elementId = "adyen-credit-card-container";

export function useAdyenEnhancedForm(
  adyenInstance: AdyenInstance["underlying"],
  translations: {
    creditCardNumber: string | TranslatedValue;
    expiryDate: string | TranslatedValue;
    securityCode: string | TranslatedValue;
  },
) {
  const [isFormReady, setIsFormReady] = useState<"initial" | "loaded" | "configured">("initial");
  const [validData, setValidData] = useEqualState<CardDataValidCallbackData | null>(null);

  const [cardData, setCardData] = useState<{
    bin: CardDataBinInfo | null;
    brand: CbObjOnBrand | null;
    supportedBrandsRaw: Array<BrandObject> | null;
  }>({
    bin: null,
    brand: null,
    supportedBrandsRaw: null,
  });

  const [state, setState] = useState<SecuredFieldsElement | null>(null);

  useEffect(() => {
    function createSecuredFieldsInstance(adyenInstance: AdyenInstance["underlying"]) {
      const onValid = (payload: unknown) => {
        //@ts-expect-error
        const { data } = payload;

        if (!data) {
          return;
        }

        const paymentMethod = (data as any)?.paymentMethod;

        if (!paymentMethod) {
          return;
        }

        const { encryptedCardNumber, encryptedExpiryMonth, encryptedExpiryYear, encryptedSecurityCode } = paymentMethod;

        if (encryptedCardNumber && encryptedExpiryMonth && encryptedExpiryYear) {
          const paymentMethodData: CardDataValidCallbackData["paymentMethod"] = {
            encryptedCardNumber: encryptedCardNumber,
            encryptedExpiryMonth: encryptedExpiryMonth,
            encryptedExpiryYear: encryptedExpiryYear,
            encryptedSecurityCode: encryptedSecurityCode,
          };

          return setValidData({
            browserInfo: data.browserInfo,
            paymentMethod: paymentMethodData,
          });
        } else {
          return;
        }
      };

      const onLoad = () => {
        setIsFormReady("loaded");
      };

      const onConfigSuccess = () => {
        setIsFormReady("configured");
      };

      const onBrand = (brand: CbObjOnBrand) => {
        // console.log(brand);

        setCardData(s => ({ ...s, brand }));
      };

      const onError = (err: any) => {
        // console.log(err);
      };

      const onBinLookup = (obj: CbObjOnBinLookup) => {
        // console.log(obj);

        const { isReset, supportedBrandsRaw } = obj;

        if (!isReset && supportedBrandsRaw && supportedBrandsRaw.length > 1) {
          setCardData(s => ({ ...s, supportedBrandsRaw: supportedBrandsRaw }));
        }
      };

      const onBinValue = (bin: CbObjOnBinValue) => {
        // console.log(bin);

        if (bin && typeof (bin as any).binValue === "string") {
          setCardData(s => ({ ...s, bin: bin as CardDataBinInfo }));
        } else {
          console.warn(`Unknown bin value ${JSON.stringify(bin)}`);
        }
      };

      const controls: AdyenInstanceControls = {
        onValid,
        onLoad,
        onConfigSuccess,
        onBrand,
        onError,
        onBinValue,
        onBinLookup,
      };

      const selector = `#${elementId}`;
      return createSecuredFields(adyenInstance, supportedBrands, styleObject, ariaLabels, controls).mount(selector);
    }

    const i = createSecuredFieldsInstance(adyenInstance);
    setState(i);

    return () => {
      //
    };
  }, [adyenInstance]);

  const isReady = isFormReady === "configured";

  return {
    isReady,
    validData: validData,
    bin: cardData.bin,
    brand: cardData.brand,
    supportedBrands: cardData.supportedBrandsRaw,
    IFrameComponent: (
      <AdyenIframeTemplate
        elementId={elementId}
        instance={state}
        cvcPolicy={cardData.brand?.cvcPolicy}
        selectedBrand={cardData.brand || null}
        isReady={isReady}
        supportedBrands={cardData.supportedBrandsRaw}
        onBrandPicked={(brand: string) => {
          if (state && brand) {
            state.dualBrandingChangeHandler(brand);
          }
        }}
        translations={translations}
      />
    ),
  };
}
